import { useState } from "react";
import { Loader2, ShieldCheck, LogOut } from "lucide-react";
import { toast } from "sonner";
import { adminApi, fmtErr } from "../api";
import { useAuth } from "../AuthContext";

const ForceChange = () => {
  const { user, logout, refresh } = useAuth();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.new_password !== form.confirm) {
      setError("New passwords do not match");
      return;
    }
    if (form.new_password === form.current_password) {
      setError("New password must be different from the current one");
      return;
    }
    setBusy(true);
    try {
      await adminApi.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password updated — welcome to the Admin Panel");
      await refresh();
    } catch (err) {
      setError(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A1128] flex items-center justify-center p-4" data-testid="force-change-page">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-3 justify-center mb-8">
          <span className="bg-[#C5221F] text-white font-display font-extrabold w-11 h-11 flex items-center justify-center">G.R</span>
          <span className="leading-tight">
            <span className="block font-display font-extrabold text-white">G. R. ENGINEERING</span>
            <span className="block text-[10px] text-slate-500 uppercase tracking-[0.3em]">Admin Panel</span>
          </span>
        </div>
        <form onSubmit={submit} className="bg-white p-8 rounded-sm shadow-2xl" data-testid="force-change-form">
          <h1 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2">
            <ShieldCheck size={17} className="text-[#C5221F]" /> Set Your Own Password
          </h1>
          <p className="mt-1.5 text-xs text-slate-500 leading-relaxed">
            For security, <strong>{user?.email}</strong> must replace the shared password before using the Admin Panel.
          </p>
          <div className="mt-6 space-y-4">
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5" htmlFor="fc-current">Current Password</label>
              <input id="fc-current" type="password" required autoComplete="current-password" data-testid="fc-current-input"
                className="w-full border border-slate-300 px-3.5 py-2.5 text-sm rounded-sm focus:outline-none focus:border-[#C5221F] focus:ring-1 focus:ring-[#C5221F]"
                value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} />
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5" htmlFor="fc-new">New Password</label>
              <input id="fc-new" type="password" required minLength={10} autoComplete="new-password" data-testid="fc-new-input"
                className="w-full border border-slate-300 px-3.5 py-2.5 text-sm rounded-sm focus:outline-none focus:border-[#C5221F] focus:ring-1 focus:ring-[#C5221F]"
                value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} />
              <p className="mt-1 text-[11px] text-slate-400">Minimum 10 characters.</p>
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5" htmlFor="fc-confirm">Confirm New Password</label>
              <input id="fc-confirm" type="password" required minLength={10} autoComplete="new-password" data-testid="fc-confirm-input"
                className="w-full border border-slate-300 px-3.5 py-2.5 text-sm rounded-sm focus:outline-none focus:border-[#C5221F] focus:ring-1 focus:ring-[#C5221F]"
                value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} />
            </div>
            {error && <p className="text-xs text-[#C5221F] bg-red-50 border border-red-200 px-3 py-2.5 rounded-sm" data-testid="force-change-error">{error}</p>}
            <button type="submit" disabled={busy} data-testid="force-change-submit-btn"
              className="w-full bg-[#C5221F] hover:bg-[#A81B18] disabled:opacity-60 text-white font-display font-semibold py-3 text-xs uppercase tracking-widest rounded-sm transition-colors duration-150 inline-flex items-center justify-center gap-2">
              {busy && <Loader2 size={14} className="animate-spin" />} Set Password & Continue
            </button>
          </div>
        </form>
        <button onClick={logout} className="mt-5 mx-auto flex items-center gap-1.5 text-[11px] text-slate-500 hover:text-white transition-colors" data-testid="force-change-logout-btn">
          <LogOut size={12} /> Sign out
        </button>
      </div>
    </div>
  );
};

export default ForceChange;
